import { createContext, ReactNode, useContext } from 'react'
import { useSearchParams } from 'react-router-dom'

const MARKETING_PARAMS_KEYS = ['websiteSourceUrl'] as const

type MarketingParams = {
  [key in typeof MARKETING_PARAMS_KEYS[number]]: string | undefined
}

const defaultValues: MarketingParams = {
  websiteSourceUrl: undefined
}

interface MarketingParamsProps {
  children: ReactNode
}

const MARKETING_PARAMS_STORAGE_KEY = 'nutrisenseMarketingParams'

export const getMarketingParamsFromStorage = (): MarketingParams | null => {
  const storedMarketingParams = sessionStorage.getItem(MARKETING_PARAMS_STORAGE_KEY)
  if (!storedMarketingParams) return null

  return JSON.parse(storedMarketingParams)
}

const setMarketingParamsToStorage = (marketingParams: MarketingParams) => {
  sessionStorage.setItem(MARKETING_PARAMS_STORAGE_KEY, JSON.stringify(marketingParams))
}

const getMarketingParams = (searchParams: URLSearchParams): MarketingParams => {
  return MARKETING_PARAMS_KEYS.reduce(
    (marketingParams, marketingParamKey: keyof MarketingParams) => {
      const searchParam = searchParams.get(marketingParamKey)
      if (searchParam) marketingParams[marketingParamKey] = searchParam

      return marketingParams
    },
    {} as MarketingParams
  )
}

const MarketingParamsContext = createContext(defaultValues)
export const useMarketingParams = () => useContext(MarketingParamsContext)

export const MarketingParamsProvider = ({ children }: MarketingParamsProps) => {
  const [searchParams] = useSearchParams()

  const marketingParams = getMarketingParams(searchParams)

  const marketingParamsFromStorage = getMarketingParamsFromStorage() || {}

  const contextValues = {
    ...marketingParamsFromStorage,
    ...marketingParams
  }

  setMarketingParamsToStorage(contextValues)

  return (
    <MarketingParamsContext.Provider value={contextValues}>
      {children}
    </MarketingParamsContext.Provider>
  )
}
